import { useEffect, useLayoutEffect, useState, useRef } from "react"
import { createPortal } from "react-dom"
import styled from "@emotion/styled"
import getMediaQuery from "css/breakpoints"
import { fullGrid } from "css/grid"
import { mvSubscribe } from "./utils"
import { useConfigurator, readLegalDisclaimer, writeLegalDisclaimer } from "./context"
import { useConsole } from "contexts/Console"
import { visuallyHidden, headline30, legend100 } from "css/text"
import { useDictionary } from "contexts/Dictionary"
import { useScroll } from "framer-motion"
import { FocusScope } from "@react-aria/focus"
import { Close } from "components/modal/ModalContainer"
import useScrollLock from "hooks/useScrollLock"
import { Personalize as UFPersonalize } from "@uniformdev/context-react"

const PPMain = styled.aside`
  ${fullGrid}
  grid-template-rows: 1fr;
  position: fixed;
  width: 100%;
  height: 100%;
  overflow: hidden;
  top: 0;
  left: 0;
  z-index: 10001;

  box-sizing: border-box;
  background: rgba(0,0,0,.6);
  padding: 2rem 0;

  ${getMediaQuery("m")} {
    grid-template-rows: 1fr auto 1fr;
  }

  & > div {
    position: relative;
    grid-column: main;
    padding-inline: var(--grid-col-unit);
    border-radius: 2rem;

    ${getMediaQuery("m")} {
      grid-column: col 2 / col -2;
      padding-inline: 6.25rem;
      grid-row: 2;
    }

    max-height: 100%;
    overflow: hidden;
    background: rgb(var(--pure-white));
    box-sizing: border-box;
    align-items: center;
    display: grid;

    & > div {
      max-height: 100%;
      overflow-y: auto;
      scrollbar-width: none;
      pointer-events: all;

      ::-webkit-scrollbar {
        display: none;
      }

      & h2 {
        margin-block-start: 6.25rem;

        &:not(:first-of-type) {
          margin-block-start: 3.75rem;
        }
      }

      & > div {
        &:last-of-type {
          margin-block-end: 6.25rem;
        }
      }
    }

    &::before, 
    &::after {
      content: "";
      position: absolute;
      left: 0;
      width: 100%;
      height: min(25vh,5rem);
      pointer-events: none;
    }
    &::before {
      top: 0;
      background: linear-gradient(0deg, rgb(var(--pure-white) / .3), rgb(var(--pure-white) / 1) 100%);
    }
    &::after {
      bottom: 0;
      background: linear-gradient(0deg, rgb(var(--pure-white) / 1), rgb(var(--pure-white) / .3) 100%);
    }

    &.prevonly.nextonly {
      &::before, 
      &::after {
        display: none;
      }
    }
    &.prevonly {
      &::after {
        display: none;
      }
    }
    &.nextonly {
      &::before {
        display: none;
      }
    }
  }
}
`

const Heading = styled.h2`
  ${headline30}
  margin-block-end: 1ex;
`

const Paragraph = styled.div`
  ${legend100}
  p:not(:last-of-type) {
    margin-block-end: 2ex;
  }
`

function PricePolicyContents({ items }) {
  const dictionary = useDictionary()
  const { legal_disclaimer_state } = useConfigurator()
  const rfmain = useRef(null)
  const rfscroll = useRef(null)
  const modalRef = useRef(null)
  const scrollLock = useScrollLock()
  const [contain, setContain] = useState(false)
  const gdpr_encoding_modes = process.browser && !!window.localStorage.getItem("gdpr_encoding_modes")
  //  const isGdprOpen = process.browser && JSON.parse(window.localStorage.getItem("isGdprOpen")) === true
  const isNotGdpr = process.browser && JSON.parse(window.localStorage.getItem("isNotGdpr")) === true

  function onClick(e) {
    legal_disclaimer_state.set(0)
  }
  /*
    function scrollTo(i) {
      const col = [...rfmain.current.querySelectorAll("li")]
      rfmain.current.scrollLeft = col[i].offsetLeft
    }
  */
  const { scrollY } = useScroll({
    container: rfscroll,
    axis: "y",
    offset: ["start end", "end end"],
  })

  function onScrollY(y) {
    const offset = 2
    const scrollmax = rfscroll.current.scrollHeight - rfscroll.current.offsetHeight

    rfmain.current.classList[y <= offset ? "add" : "remove"]("nextonly")
    rfmain.current.classList[y >= (scrollmax - offset) ? "add" : "remove"]("prevonly")
  }
  useLayoutEffect(() => mvSubscribe(scrollY, onScrollY), [items])

  useEffect(() => {
    const r = modalRef.current
    scrollLock.lock(r)
    return () => scrollLock.unlock(r)
  }, [])

  useLayoutEffect(() => {
    const r = rfmain.current
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (entry.contentBoxSize) {
          onScrollY(scrollY.get())
        }
      }
    })

    resizeObserver.observe(r)

    return () => resizeObserver.unobserve(r)
  }, [])

  useLayoutEffect(() => {
    if (isNotGdpr || gdpr_encoding_modes) return setContain(true)
    window.addEventListener("gdprClosed", onGdpr)

    return () => window.removeEventListener("gdprClosed", onGdpr)

    function onGdpr() {
      setContain(true)
    }
  }, [gdpr_encoding_modes, isNotGdpr])

  useLayoutEffect(() => {
    if (!contain) return
    setTimeout(() => {
      const btn = rfmain.current.querySelector("button")
      if (btn) btn.focus()
    }, 400)
  }, [contain])

  return (
    <PPMain ref={modalRef}>
      <FocusScope contain={contain}>
        <div ref={rfmain}>
          <Close aria-label={dictionary.popinClose()} onClick={onClick} icon='close' className='icon translucent-light'>
            <span>{dictionary.popinClose()}</span>
          </Close>
          <div ref={rfscroll} tabIndex="0">
            {items.map((item, i) => (
              <PricePolicyBlock key={`pp-${i}`} {...item} />
            ))}
          </div>
        </div>
      </FocusScope>
    </PPMain>
  )
}

function PricePolicyBlock({ title, text }) {
  return (
    <>
      <Heading>{title}</Heading>
      <Paragraph dangerouslySetInnerHTML={{ __html: text }} />
    </>
  )
}

function Variation({ content }) {
  const console = useConsole()
  const [active, setActive] = useState(null)
  const { legal_disclaimer_state } = useConfigurator()

  useLayoutEffect(() => {
    if (legal_disclaimer_state.get() < 0) legal_disclaimer_state.set(1)
  }, [])

  async function onState(v) {
    if (v === 0) return setActive(null)
    writeLegalDisclaimer(0)
    const info = [
      {
        title: content?.[0]?.price_title,
        text: content?.[0]?.legal_notice
      },
      {
        title: content?.[0]?.scarcity_title,
        text: content?.[0]?.scarcity_text
      }
    ]
    setActive(<PricePolicyContents items={info} />)
  }
  useLayoutEffect(() => mvSubscribe(legal_disclaimer_state, onState), [])

  return process.browser && active ? createPortal(active, document.body) : null
}

export default function P13N(props) {
  const console = useConsole()
  const { legal_disclaimer } = props

  const variations = legal_disclaimer.reduce((variations, variation, i) => {
    const {
      criteria: { name: pz = null },
      content,
    } = variation
    !!content && variations.push(Object.assign({ content }, pz && { pz }))
    return variations.sort((a, b) => +!!b?.pz - +!!a?.pz) /** move default variant at the end of the array */
  }, [])

  return <UFPersonalize variations={variations} component={Variation} />
}
